
import { Component, Vue, Prop } from 'vue-property-decorator';
import { getFormatDate } from "@/utils/formatDates";
import { Advertiser, IProject, PROJECTS } from "@/services/advertiser/Advertiser";
import { translateAdvertiserStatus, translateBoolean } from "@/utils/translate";
import BaseSelect from "@/components-UI/BaseSelect.vue";
import { ITag } from "@/api/types/revenue";
import { namespace } from "vuex-class";
import { ICurrency } from "@/api/types/catalogue";
import { getActiveUserSearch } from "@/api/user";
import BaseTooltip from '@/components-UI/BaseTooltip.vue';
import BaseEditableCard from "@/components-UI/BaseEditableCard.vue";
import BaseList from "@/components-UI/list/BaseList.vue";
import BaseListItem from "@/components-UI/list/BaseListItem.vue";
import BaseAutocomplete from '@/components-UI/BaseAutocomplete.vue';
import BaseTextarea from "@/components-UI/BaseTextarea.vue";

const catalogue = namespace('catalogueModule');

enum Keys {
    Name = 'name',
    Status = 'status',
    Manager = 'manager_id',
    Tag = 'tag',
    Projects = 'projects',
    Currency = 'currency',
    Checker = 'is_checker_used',
    Vat = 'vat',
    EmailLanguage = 'email_language',
    Note = 'note',
    SendTo1C = 'send_invoices_to_1c',
}
type SelectItem = ITag | IProject | ICurrency | { value: string | boolean, label: string };

@Component({
    methods: {
        translateBoolean,
        getFormatDate,
        translateAdvertiserStatus,
        getActiveUserSearch,
    },
    components: {
        BaseTextarea,
        BaseListItem,
        BaseList,
        BaseEditableCard,
        BaseAutocomplete,
        BaseSelect,
        BaseTooltip,
    },
})

export default class AdvertiserAbout extends Vue {
    @catalogue.Getter('GET_CURRENCIES') currencies!: ICurrency[];
    @Prop({ required: true }) advertiser!: Advertiser;
    @Prop() disableTitleButton!: boolean;

    isEdit: boolean = false;
    advertiserData = {};
    selectKey = 0;
    Keys = Keys;
    selects = [Keys.Status, Keys.Tag, Keys.Currency, Keys.Projects, Keys.EmailLanguage, Keys.SendTo1C];
    singleValueSelects = [Keys.EmailLanguage, Keys.SendTo1C];
    editable = [...this.selects, Keys.Checker, Keys.Name, Keys.Vat, Keys.Note];

    get currencyItems(): ICurrency[] {
        if (this.currencies.some((item) => item.code === 'USDT')) {
            return this.currencies;
        }

        const items = [...this.currencies];
        const usdIndex = items.findIndex((item) => item.code === 'USD');
        items.splice(usdIndex + 1, 0, { code: 'USDT', symbol: 'USDT', name: 'Tether' });

        return items;
    }

    get getOptions(): { name: string, value: any, key: Keys, isHidden: boolean, hint?: string  }[] {
        const options = [
            { name: 'Название', value: this.advertiser.name, key: Keys.Name, isHidden: true },
            { name: 'Статус', value: this.advertiser.status, key: Keys.Status, isHidden: true },
            { name: 'Менеджер', value: this.getManagerName, key: Keys.Manager, isHidden: false },
            { name: 'Тег', value: this.advertiser.tag, key: Keys.Tag, isHidden: false },
            { name: 'Проект', value: this.advertiser.projects, key: Keys.Projects, isHidden: false },
            { name: 'НДС', value: this.advertiser.vat, key: Keys.Vat, isHidden: false },
            { name: 'Памятка', value: this.advertiser.note, key: Keys.Note, isHidden: false, hint: 'Отображается на странице создания счёта' },
            { name: 'Валюта', value: this.advertiser.currency, key: Keys.Currency, isHidden: false },
            { name: 'Чеккер', value: this.advertiser.is_checker_used, key: Keys.Checker, isHidden: false },
            { name: 'Язык писем', value: this.advertiser.email_language, key: Keys.EmailLanguage, isHidden: false },
            { name: 'Отправка в 1С', value: this.advertiser.send_invoices_to_1c, key: Keys.SendTo1C, isHidden: false },
        ];

        if (this.isEdit) {
            return options;
        } else {
            return options.filter((item) => !item.isHidden);
        }
    }

    getSelectOptions(key: string): { itemValue: string, itemText: string, template?: (item: any) => string, items: SelectItem[], isMultiple: boolean, isClearable?: boolean } {
        const options = {
            status: {
                itemValue: 'value',
                itemText: 'value',
                items: this.getStatuses,
                template: (status: any) => translateAdvertiserStatus(status.value).text,
                isMultiple: false,
            },
            tag: { itemValue: 'id', itemText: 'name', items: this.advertiser.allTags, isMultiple: false },
            projects: { itemValue: 'abbr', itemText: 'text', template: (item: IProject) => `${item.abbr} - ${item.text}`, items: PROJECTS, isMultiple: true },
            currency: { itemValue: 'code', itemText: 'code', items: this.currencyItems, isMultiple: true },
            email_language: {
                itemValue: 'value',
                itemText: 'label',
                items: [
                    { value: 'ru', label: 'Русский' },
                    { value: 'en', label: 'Английский' },
                    { value: 'es', label: 'Испанский' },
                ],
                isMultiple: false,
                isClearable: false,
            },
            send_invoices_to_1c: {
                itemValue: 'value',
                itemText: 'label',
                items: [
                    { value: true, label: 'Отправлять' },
                    { value: false, label: 'Не отправлять' },
                ],
                isMultiple: false,
                isClearable: false,
            },
        };

        return options[key];
    }

    get getStatuses(): { id: number, value: string }[] {
        return [
            { id: 1, value: 'active' },
            { id: 2, value: 'deleted' },
            { id: 3, value: 'blocked' },
            { id: 4, value: 'rejected' },
        ];
    }

    get getManagerName(): string {
        if (!this.advertiser || !this.advertiser.managers?.length) return '';
        const manager = this.advertiser.account_manager!;
        return `${manager.id} ${manager.full_name}` || `${manager.id} ${manager.full_name} ${manager.last_name}` || '';
    }

    get getManagerId(): number | string {
        if (!this.advertiser || !this.advertiser.managers?.length) return '';
        const manager = this.advertiser.account_manager!;
        return manager.id || '';
    }

    setManager(id: number): void {
        this.advertiserData[Keys.Manager] = id;
    }

    onSelectFieldUpdate(key: Keys, value: unknown): void {
        this.$set(this.advertiserData, key, value);
    }

    editInfo(edit: boolean): void {
        if (edit) {
            this.setValues();
        } else {
            this.advertiser.editMainInfo(this.advertiserData);
        }
        this.isEdit = edit;
    }

    cancelEdit(): void {
        this.isEdit = false;
    }

    setValues(): void {
        this.advertiserData[Keys.Manager] = this.getManagerId;
        this.advertiserData[Keys.Note] = this.advertiser.note;
        this.editable.forEach(id => {
            if (id === Keys.EmailLanguage) {
                this.advertiserData[id] = this.advertiser.email_language || 'ru';
                return;
            }
            if (id === Keys.SendTo1C) {
                this.advertiserData[id] = this.advertiser.send_invoices_to_1c;
                return;
            }
            if (this.advertiser[id]) {
                const value = JSON.parse(JSON.stringify(this.advertiser?.[id]));
                this.advertiserData[id] = id === Keys.Tag ? value.id : value;
            }
        });
    }

    getValue(key: Keys): string[] {
        const values = this.advertiser[key];
        const { items, itemValue, itemText } = this.getSelectOptions(key);
        if (key === Keys.EmailLanguage) {
            const code = values || 'ru';
            const label = items.find((item: SelectItem) => (item as { value: string }).value === code)?.[itemText];
            return label ? [label as string] : [];
        }
        if (key === Keys.SendTo1C) {
            const label = items.find((item: SelectItem) => (item as { value: boolean }).value === values)?.[itemText];
            return label ? [label as string] : [];
        }
        if (!values || values.length === 0) return [];
        if (key === Keys.Tag) return [values[itemText]];
        if (key === Keys.Status) return [values];
        return values.map((i: any) => items.find((item: SelectItem) => item[itemValue] === i)?.[itemText]!);
    }
}
